
const ALGO = "AES-GCM"; // Web Crypto API format
const IV_LENGTH = 12;
const SALT_LENGTH = 16;
const KEY_LENGTH = 32;
const AUTH_TAG_LENGTH = 16;
// const PBKDF2_ITERATIONS = 150000;
const PBKDF2_ITERATIONS = 100_000;
const DIGEST = "sha256";
const ENCRYPTION_SECRET = "dev_dummy_secret_9F3kL#2sP@q8XzA";
const HMAC_SECRET = "hmac_dummy_secret_@9X2";

if (!ENCRYPTION_SECRET || !HMAC_SECRET) {
  throw new Error("Missing encryption env secrets");
}

function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

async function deriveKey(secret, salt) {
  const secretKey = new TextEncoder().encode(secret);
  
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    secretKey,
    'PBKDF2',
    false,
    ['deriveBits', 'deriveKey']
  );
  
  // Map DIGEST to Web Crypto API format
  const hashAlgo = DIGEST === 'sha256' ? 'SHA-256' : DIGEST.toUpperCase();
  
  const key = await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt,
      iterations: PBKDF2_ITERATIONS,
      hash: hashAlgo
    },
    keyMaterial,
    {
      name: ALGO,
      length: KEY_LENGTH * 8 // KEY_LENGTH is in bytes, convert to bits
    },
    false,
    ['encrypt']
  );
  
  return key;
}

async function sign(envelope) {
  const encoder = new TextEncoder();
  const keyData = encoder.encode(HMAC_SECRET);
  const messageData = encoder.encode(envelope);
  
  // Map DIGEST to Web Crypto API format
  const hashAlgo = DIGEST === 'sha256' ? 'SHA-256' : DIGEST.toUpperCase();
  
  const key = await crypto.subtle.importKey(
    'raw',
    keyData,
    {
      name: 'HMAC',
      hash: hashAlgo
    },
    false,
    ['sign']
  );
  
  const signature = await crypto.subtle.sign('HMAC', key, messageData);
  
  // Convert ArrayBuffer to hex string
  const hashArray = Array.from(new Uint8Array(signature));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// export async function hybridEncrypt(payload) {
//   if (!payload) throw new Error("Payload missing");
  
//   try {
//     // Generate random salt and IV
//     const salt = crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
//     const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
    
//     // Generate nonce and timestamp
//     const nonce = generateUUID();
//     const timestamp = Date.now();
    
//     // Derive key from secret and salt
//     const key = await deriveKey(ENCRYPTION_SECRET, salt);
    
//     // Prepare data to encrypt
//     const data = JSON.stringify({
//       payload,
//       nonce,
//       timestamp
//     });
    
//     // Encrypt using AES-GCM
//     // Note: In Web Crypto API, AES-GCM automatically appends the auth tag to the ciphertext
//     const encrypted = await crypto.subtle.encrypt(
//       {
//         name: ALGO,
//         iv: iv
//       },
//       key,
//       new TextEncoder().encode(data)
//     );
    
//     // Extract auth tag and ciphertext
//     // In Web Crypto API with AES-GCM, the last 16 bytes are the auth tag
//     const encryptedArray = new Uint8Array(encrypted);
//     const encryptedData = encryptedArray.slice(0, -AUTH_TAG_LENGTH);
//     const authTag = encryptedArray.slice(-AUTH_TAG_LENGTH);
    
//     // Create envelope: salt + iv + authTag + encrypted data
//     const envelope = btoa(
//       String.fromCharCode(
//         ...new Uint8Array([
//           ...salt,
//           ...iv,
//           ...authTag,
//           ...encryptedData
//         ])
//       )
//     );
    
//     // Sign the envelope
//     const signature = await sign(envelope);
    
//     return {
//       envelope,
//       signature
//     };
//   } catch (error) {
//     console.error("Error encrypting payload:", error);
//     throw error;
//   }
// }
export async function hybridEncrypt(payload) {
  if (!payload) throw new Error("Payload missing");

  try {
    // Generate random salt and IV
    const salt = crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
    const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));

    // Generate nonce and timestamp
    const nonce = generateUUID();
    const timestamp = Date.now();

    // Derive key from secret and salt
    const key = await deriveKey(ENCRYPTION_SECRET, salt);

    // Prepare data to encrypt
    const data = JSON.stringify({
      payload,
      nonce,
      timestamp
    });

    // Encrypt using AES-GCM
    const encrypted = await crypto.subtle.encrypt(
      {
        name: ALGO,
        iv
      },
      key,
      new TextEncoder().encode(data)
    );

    // Extract auth tag and ciphertext
    // Web Crypto AES-GCM appends auth tag at the end
    const encryptedArray = new Uint8Array(encrypted);
    const encryptedData = encryptedArray.slice(0, -AUTH_TAG_LENGTH);
    const authTag = encryptedArray.slice(-AUTH_TAG_LENGTH);

    // Combine salt + iv + authTag + encrypted data
    const combined = new Uint8Array(
      salt.length + iv.length + authTag.length + encryptedData.length
    );

    let offset = 0;
    combined.set(salt, offset);
    offset += salt.length;

    combined.set(iv, offset);
    offset += iv.length;

    combined.set(authTag, offset);
    offset += authTag.length;

    combined.set(encryptedData, offset);

    // Safe base64 encoding (chunked)
    const envelope = uint8ToBase64(combined);

    // Sign the envelope
    const signature = await sign(envelope);

    return {
      envelope,
      signature
    };
  } catch (error) {
    console.error("Error encrypting payload:", error);
    throw error;
  }
}

/**
 * Safely converts Uint8Array to Base64 without stack overflow
 */
function uint8ToBase64(uint8) {
  let binary = "";
  const chunkSize = 0x8000; // 32 KB

  for (let i = 0; i < uint8.length; i += chunkSize) {
    binary += String.fromCharCode(
      ...uint8.subarray(i, i + chunkSize)
    );
  }

  return btoa(binary);
}

export async function encryptPayload(payload) {
  // Skip encryption for FormData (can't encrypt binary data this way)
  if (payload instanceof FormData) {
    return payload;
  }
  
  // Skip encryption if payload is null or undefined
  if (payload === null || payload === undefined) {
    return payload;
  }
  
  try {
    return await hybridEncrypt(payload);
  } catch (error) {
    console.error("Encryption failed:", error);
    // Return original payload if encryption fails
    return payload;
  }
}
